"use client";

import { useState, useEffect } from "react";

// Path definitions: 9 routes (3 sources × 3 destinations)
const PATHS = {
  // USDC (top-left) routes
  USDC_TO_GHANA: "M54,0.5 L157,0.5 L164.063,4.8864 L267.6,143.299 L428,144 L541,143 L644.537,4.8864 L645,0.5 L748,0.5",
  USDC_TO_NIGERIA: "M54,0.5 L157,0.5 L164.063,4.8864 L267.6,143.299 L428,144 L856,144.5",
  USDC_TO_KENYA: "M54,0.5 L157,0.5 L164.063,4.8864 L267.6,143.299 L428,144 L582.646,144.354 L678.646,240.354 L678,237.5 L781,237.5",

  // BTC (middle-left) routes
  BTC_TO_GHANA: "M0,144.5 L428,144 L541,143 L644.537,4.8864 L645,0.5 L748,0.5",
  BTC_TO_NIGERIA: "M0,144.5 L856,144.5",
  BTC_TO_KENYA: "M0,144.5 L428,144 L582.646,144.354 L678.646,240.354 L678,237.5 L781,237.5",

  // USDT (bottom-left) routes
  USDT_TO_GHANA: "M119,237.5 L222,237.5 L224.654,236.639 L322.654,142.639 L428,144 L541,143 L644.537,4.8864 L645,0.5 L748,0.5",
  USDT_TO_NIGERIA: "M119,237.5 L222,237.5 L224.654,236.639 L322.654,142.639 L428,144 L856,144.5",
  USDT_TO_KENYA: "M119,237.5 L222,237.5 L224.654,236.639 L322.654,142.639 L428,144 L582.646,144.354 L678.646,240.354 L678,237.5 L781,237.5",
} as const;

type PathKey = keyof typeof PATHS;

// Destination rotation for each dot
const DOT_DESTINATIONS: Record<string, PathKey[]> = {
  dot1: ["USDC_TO_KENYA", "USDC_TO_GHANA", "USDC_TO_NIGERIA"],
  dot2: ["BTC_TO_NIGERIA", "BTC_TO_KENYA", "BTC_TO_GHANA"],
  dot3: ["USDT_TO_GHANA", "USDT_TO_NIGERIA", "USDT_TO_KENYA"],
};

// Animation configs for each dot (different speeds - slower for better visibility)
const DOT_CONFIGS: Record<string, { duration: number; delay: number }> = {
  dot1: { duration: 6, delay: 0.3 },
  dot2: { duration: 7.5, delay: 1.2 },
  dot3: { duration: 6.8, delay: 2 },
};

interface AnimatedDotProps {
  dotKey: string;
  pathId: string;
}

function AnimatedDot({ dotKey, pathId }: AnimatedDotProps) {
  const config = DOT_CONFIGS[dotKey];

  return (
    <circle r="6" fill="#0095FF" filter="url(#glow)" opacity="0">
      <animateMotion
        dur={`${config.duration}s`}
        repeatCount="1"
        fill="freeze"
        begin={`${config.delay}s`}
        calcMode="linear"
      >
        <mpath href={`#${pathId}`} />
      </animateMotion>
      <animate
        attributeName="opacity"
        values="0;1;1;1;0"
        keyTimes="0;0.05;0.5;0.95;1"
        dur={`${config.duration}s`}
        begin={`${config.delay}s`}
        fill="freeze"
      />
    </circle>
  );
}

export default function AnimatedDots() {
  const [destinationIndices, setDestinationIndices] = useState({
    dot1: 0,
    dot2: 0,
    dot3: 0,
  });
  const [animationKey, setAnimationKey] = useState(0);

  // Calculate max duration including delays
  const maxDuration = Math.max(
    DOT_CONFIGS.dot1.duration + DOT_CONFIGS.dot1.delay,
    DOT_CONFIGS.dot2.duration + DOT_CONFIGS.dot2.delay,
    DOT_CONFIGS.dot3.duration + DOT_CONFIGS.dot3.delay
  );

  // Cycle to next destinations after animation completes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDestinationIndices((prev) => ({
        dot1: (prev.dot1 + 1) % DOT_DESTINATIONS.dot1.length,
        dot2: (prev.dot2 + 1) % DOT_DESTINATIONS.dot2.length,
        dot3: (prev.dot3 + 1) % DOT_DESTINATIONS.dot3.length,
      }));
      setAnimationKey((prev) => prev + 1);
    }, (maxDuration + 0.5) * 1000); // Add small buffer

    return () => clearTimeout(timer);
  }, [animationKey, maxDuration]);

  const dot1Path = DOT_DESTINATIONS.dot1[destinationIndices.dot1];
  const dot2Path = DOT_DESTINATIONS.dot2[destinationIndices.dot2];
  const dot3Path = DOT_DESTINATIONS.dot3[destinationIndices.dot3];

  return (
    <svg
      key={animationKey}
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 856 241"
      fill="none"
      style={{ overflow: "visible" }}
    >
      <defs>
        {/* Glow filter for dots */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Define all paths */}
        {Object.entries(PATHS).map(([key, path]) => (
          <path key={key} id={key} d={path} />
        ))}
      </defs>

      {/* Animated dots */}
      <AnimatedDot dotKey="dot1" pathId={dot1Path} />
      <AnimatedDot dotKey="dot2" pathId={dot2Path} />
      <AnimatedDot dotKey="dot3" pathId={dot3Path} />
    </svg>
  );
}
